import React from "react";
import { Card, CardContent } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Filter, X, Building2, Calendar } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useTran } from "@/hooks";

const CarbonDashboardFilters = ({
  filters,
  onFiltersChange,
  organizations = [],
  selectedOrgId,
  onOrgChange,
  submissionCounts = {},
  selectedYear,
  onYearChange,
  yearOptions = [],
  role,
}) => {
  const updateFilter = (key, value) => {
    onFiltersChange({ ...filters, [key]: value });
  };
  const { t } = useTran();

  const clearFilters = () => {
    onFiltersChange({
      reviewStatus: "all",
      consentStatus: "all",
      usableOnly: false,
    });
  };

  const hasActiveFilters = filters.reviewStatus !== "all";

  const flattenOrgs = (orgs, level = 0, parentPath = "") => {
    const result = [];
    orgs.forEach((org) => {
      const path = parentPath ? `${parentPath} > ${org.name}` : org.name;
      result.push({
        id: org.id,
        name: org.name,
        level,
        path,
        count: submissionCounts[org.id] || 0,
      });
      if (org.children && org.children.length > 0) {
        result.push(...flattenOrgs(org.children, level + 1, path));
      }
    });
    return result;
  };

  const flatOrgs = flattenOrgs(organizations);
  const totalSubmissions = Object.values(submissionCounts).reduce(
    (sum, count) => sum + count,
    0,
  );
  return (
    <Card className="border-slate-200 dark:border-slate-800">
      <CardContent className="p-4">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-slate-500" />
            <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
              {t("Filters")}
            </span>
            {hasActiveFilters && (
              <Badge variant="outline" className="ml-2">
                {t("Active")}
              </Badge>
            )}
          </div>

          <div className="flex flex-1 flex-wrap items-center gap-3 lg:justify-end">
            <div className="flex items-center gap-2">
              <Building2 className="h-4 w-4 text-slate-500" />
              <label className="text-xs text-slate-600 dark:text-slate-400">
                {t("Organization:")}
              </label>
              <Select
                value={selectedOrgId || "all"}
                onValueChange={(val) => onOrgChange(val === "all" ? null : val)}
                disabled={role === "sme_user" ? true : false}
              >
                <SelectTrigger className="w-[240px] h-8 text-xs">
                  <SelectValue>
                    {selectedOrgId ? (
                      <div className="flex items-center justify-between w-full">
                        <span>
                          {flatOrgs.find((o) => o.id === selectedOrgId)?.name ||
                            "Select"}
                        </span>
                        {submissionCounts[selectedOrgId] > 0 && (
                          <Badge
                            variant="outline"
                            className="ml-2 text-[10px] h-4"
                          >
                            {submissionCounts[selectedOrgId]}
                          </Badge>
                        )}
                      </div>
                    ) : (
                      <div className="flex items-center justify-between w-full">
                        <span>{t("All Organizations")}</span>
                        <Badge className="ml-2 bg-emerald-600 text-[10px] h-4">
                          {totalSubmissions}
                        </Badge>
                      </div>
                    )}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent className="max-h-[300px]">
                  <SelectItem value="all">
                    <div className="flex items-center justify-between w-full">
                      <span className="font-medium">
                        {t("All Organizations")}
                      </span>
                      <Badge className="ml-4 bg-emerald-600 text-white text-[10px] h-4">
                        {totalSubmissions}
                      </Badge>
                    </div>
                  </SelectItem>
                  {flatOrgs.map((org) => (
                    <SelectItem key={org.id} value={org.id}>
                      <div className="flex items-center justify-between w-full">
                        <span
                          style={{ paddingLeft: `${org.level * 12}px` }}
                          className="flex-1 text-xs"
                        >
                          {org.level > 0 && (
                            <span className="text-slate-400 mr-2">└</span>
                          )}
                          {org.name}
                        </span>
                        {org.count > 0 && (
                          <Badge
                            variant="outline"
                            className="ml-2 text-[10px] h-4"
                          >
                            {org.count}
                          </Badge>
                        )}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Year Selector */}
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-slate-500" />
              <label className="text-xs text-slate-600 dark:text-slate-400">
                {t("Year:")}
              </label>
              <Select
                value={selectedYear === "all" ? "all" : String(selectedYear)}
                onValueChange={(val) =>
                  onYearChange(val === "all" ? "all" : Number(val))
                }
              >
                <SelectTrigger className="w-[120px] h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>

                <SelectContent>
                  <SelectItem value="all">All years</SelectItem>
                  {yearOptions.map((y) => (
                    <SelectItem key={y} value={String(y)}>
                      {y === "all" ? "All Years" : y}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Bank Review Status Filter */}
            {/* <div className="flex items-center gap-2">
              <label className="text-xs text-slate-600 dark:text-slate-400">
                Bank Review:
              </label>
              <Select
                value={filters.reviewStatus}
                onValueChange={(val) => updateFilter('reviewStatus', val)}
              >
                <SelectTrigger className="w-[120px] h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                </SelectContent>
              </Select>
            </div> */}

            {hasActiveFilters && (
              <Button
                variant="ghost"
                size="sm"
                className="h-8 px-3 text-xs"
                onClick={clearFilters}
              >
                <X className="h-3 w-3 mr-1" />
                Clear
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default CarbonDashboardFilters;
